import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, timer } from 'rxjs';
import {
  switchMap,
  distinctUntilChanged,
  takeWhile,
  retry,
  map,
} from 'rxjs/operators';
import {
  BulkProgressResponse,
  ChildApplicationsResponse,
  ValidationStatus,
  ProgressState,
} from '../models/bulk-progress.models';

interface Environment {
  apiGatewayBaseUrl: string;
}

@Injectable()
export class BulkProgressService {
  private readonly baseUrl: string;

  constructor(
    private http: HttpClient,
    @Inject('environment') private environment: Environment
  ) {
    this.baseUrl = `${this.environment.apiGatewayBaseUrl}/application/v1/applications`;
  }

  /**
   * Poll progress endpoint at specified interval
   * Stops polling when status is COMPLETED, PARTIAL_FAILURE, or COMPLETE_FAILURE
   */
  pollProgress(
    applicationNumber: string,
    interval: number
  ): Observable<BulkProgressResponse> {
    return timer(0, interval).pipe(
      switchMap(() => {
        return this.fetchProgress(applicationNumber);
      }),
      map(response => {
        return response;
      }),
      distinctUntilChanged(
        (prev, curr) =>
          prev.status === curr.status &&
          prev.percentageComplete === curr.percentageComplete &&
          prev.billId === curr.billId
      ),
      takeWhile(response => {
        const totalProcessed =
          response.totalSuccessfulChildApplications +
          response.totalFailedChildApplications;
        const allItemsProcessed =
          totalProcessed === response.totalExpectedChildApplications &&
          totalProcessed > 0;
        const noBillId = !response.billId || response.billId === '';
        const waitingForBillId = allItemsProcessed && noBillId;

        const shouldContinue =
          response.status === ProgressState.IN_PROGRESS || waitingForBillId;

        return shouldContinue;
      }, true),
      retry({
        count: 3,
        delay: (error, retryCount) => {
          console.error('[BulkProgressService] Error, retrying...', error);
          return timer(retryCount * 1000);
        },
      })
    );
  }

  /**
   * Fetch current progress status
   */
  private fetchProgress(
    applicationNumber: string
  ): Observable<BulkProgressResponse> {
    const url = `${this.baseUrl}/${applicationNumber}/bulk-progress`;
    return this.http
      .get<{ data: BulkProgressResponse }>(url)
      .pipe(map(response => response.data));
  }

  /**
   * Fetch failed child applications
   */
  fetchFailedApplications(
    applicationNumber: string
  ): Observable<ChildApplicationsResponse> {
    const url = `${this.baseUrl}/${applicationNumber}/child-applications`;
    const params = new HttpParams().set(
      'validationStatus',
      ValidationStatus.FAILED
    );
    return this.http
      .get<{ data: ChildApplicationsResponse }>(url, { params })
      .pipe(map(response => response.data));
  }

  /**
   * Fetch successful child applications (if needed for future features)
   */
  fetchSuccessfulApplications(
    applicationNumber: string
  ): Observable<ChildApplicationsResponse> {
    const url = `${this.baseUrl}/${applicationNumber}/child-applications`;
    const params = new HttpParams().set(
      'validationStatus',
      ValidationStatus.SUCCESS
    );
    return this.http
      .get<{ data: ChildApplicationsResponse }>(url, { params })
      .pipe(map(response => response.data));
  }
}
